import { StatusBar } from "expo-status-bar";
import {
  Pressable,
  Platform,
  StyleSheet,
  ScrollView,
  Text,
  TextInput,
  useColorScheme,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { useLinkTo } from "@react-navigation/native";
import { View } from "../components/Themed";
import { useQuery, useLazyQuery, gql, useMutation } from "@apollo/client";
import Config from "../constants/Config";
import { arrayBufferToBase64Url, base64UrlToArrayBuffer } from "../utils/base64url";

import { Picker } from "@react-native-picker/picker";

const LOCATIONS_QUERY = gql`
  query getLocations($token: String!) {
    locations(token: $token) {
      id
      name
      city
    }
  }
`;

const LOGIN = gql`
  mutation login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      adminToken
      apiToken
      role
    }
  }
`;

const PASSKEY_AUTH_OPTIONS = gql`
  query passkeyAuthenticationOptions {
    passkeyAuthenticationOptions {
      challenge
      rpId
      timeout
      allowCredentials
    }
  }
`;

const LOGIN_WITH_PASSKEY = gql`
  mutation loginWithPasskey(
    $credentialId: String!
    $clientDataJSON: String!
    $authenticatorData: String!
    $signature: String!
  ) {
    loginWithPasskey(
      credentialId: $credentialId
      clientDataJSON: $clientDataJSON
      authenticatorData: $authenticatorData
      signature: $signature
    ) {
      token
      adminToken
      apiToken
      role
    }
  }
`;

export default function OperatorSettingsScreen() {
  const [operatorId, setOperatorId] = useState("");
  const [locationId, setLocationId] = useState("All");
  const [swipeAction, setSwipeAction] = useState("Deliver");
  const [role, setRole] = useState("");
  const [token, setToken] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passkeySupported, setPasskeySupported] = useState(false);
  const linkTo = useLinkTo();
  const colorScheme = useColorScheme();

  const { loading, error, data } = useQuery(LOCATIONS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [getAuthOptions] = useLazyQuery(PASSKEY_AUTH_OPTIONS);
  const [loginWithPasskeyMutation] = useMutation(LOGIN_WITH_PASSKEY);

  useEffect(() => {
    async function fetchData() {
      try {
        const storedId = await AsyncStorage.getItem("operatorId");
        setOperatorId(storedId);
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      try {
        const swipeAction = await AsyncStorage.getItem("orderItemSwipe");
        setSwipeAction(swipeAction)
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      try {
        const locationId = await AsyncStorage.getItem("location");
        if (locationId) {
          setLocationId(locationId);
        }
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      try {
        const storedRole = await AsyncStorage.getItem("role");
        setRole(storedRole);
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      try {
        const storedAdminToken = await AsyncStorage.getItem("adminToken");
        setAdminToken(storedAdminToken);
      } catch (ex) { console.warn("AsyncStorage read error:", ex); }

      // Check if passkeys are supported
      if (typeof window !== 'undefined' && window.PublicKeyCredential) {
        setPasskeySupported(true);
      }
    }
    fetchData();
  }, []);

  const [errorMessage, setErrorMessage] = useState("");

  const [login] = useMutation(LOGIN);

  async function tryLogin(username, password) {
    const result = await login({
      variables: {
        username,
        password,
      },
    });

    if (result && result.data && result.data.login) {
      setUsername("");
      setPassword("");
      if (result.data.login.adminToken) {
        try {
          await AsyncStorage.setItem(
            "adminToken",
            result.data.login.adminToken
          );
          setAdminToken(result.data.login.adminToken);
        } catch (ex) { console.warn("Failed to store adminToken:", ex); }
      }
      if (result.data.login.apiToken) {
        try {
          await AsyncStorage.setItem("token", result.data.login.apiToken);
          setToken(result.data.login.apiToken);
        } catch (ex) { console.warn("Failed to store token:", ex); }
      }

      if (result.data.login.token) {
        try {
          await AsyncStorage.setItem("operatorId", result.data.login.token);
          setOperatorId(result.data.login.token);
        } catch (ex) { console.warn("Failed to store operatorId:", ex); }
      }

      if (result.data.login.role) {
        try {
          await AsyncStorage.setItem("role", result.data.login.role);
          setRole(result.data.login.role);
        } catch (ex) { console.warn("Failed to store role:", ex); }
      }
      setErrorMessage("");
    } else {
      setErrorMessage("Check your credentials and try again");
    }
    console.log(result);
  }

  async function loginWithPasskey() {
    setErrorMessage("");

    try {
      if (typeof navigator === "undefined" || !navigator.credentials?.get) {
        setErrorMessage("Passkey authentication is not available on this device");
        return;
      }

      // Get authentication options from server (no username needed for discoverable credentials)
      const { data } = await getAuthOptions({ fetchPolicy: 'network-only' });
      const options = data?.passkeyAuthenticationOptions;

      if (!options) {
        setErrorMessage("Failed to get passkey options");
        return;
      }

      // Get credential using discoverable credentials (passkey will identify the user)
      const publicKeyOptions = {
        challenge: base64UrlToArrayBuffer(options.challenge),
        rpId: options.rpId,
        timeout: options.timeout,
        userVerification: "preferred",
      };

      // Only add allowCredentials if the server provided specific credentials
      if (options.allowCredentials && options.allowCredentials.length > 0) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map((id) => ({
          type: "public-key",
          id: base64UrlToArrayBuffer(id),
        }));
      }

      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      if (!credential) {
        setErrorMessage("Passkey authentication cancelled");
        return;
      }

      // Send to server
      const response = credential.response;
      const result = await loginWithPasskeyMutation({
        variables: {
          credentialId: arrayBufferToBase64Url(credential.rawId),
          clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
          authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
          signature: arrayBufferToBase64Url(response.signature),
        },
      });

      if (result?.data?.loginWithPasskey) {
        const loginData = result.data.loginWithPasskey;
        setUsername("");

        if (loginData.adminToken) {
          await AsyncStorage.setItem("adminToken", loginData.adminToken);
          setAdminToken(loginData.adminToken);
        }
        if (loginData.apiToken) {
          await AsyncStorage.setItem("token", loginData.apiToken);
          setToken(loginData.apiToken);
        }
        if (loginData.token) {
          await AsyncStorage.setItem("operatorId", loginData.token);
          setOperatorId(loginData.token);
        }
        if (loginData.role) {
          await AsyncStorage.setItem("role", loginData.role);
          setRole(loginData.role);
        }
        setErrorMessage("");
      } else {
        setErrorMessage("Passkey authentication failed");
      }
    } catch (err) {
      console.error("Passkey login error:", err);
      setErrorMessage(err.message || "Passkey authentication failed");
    }
  }

  const GridTile = ({ icon, label, onPress, color = "#007AFF", bgColor = "#e3f2fd" }) => (
    <Pressable style={[styles.gridTile, { backgroundColor: bgColor }]} onPress={onPress}>
      <View style={[styles.tileIconContainer, { backgroundColor: color }]}>
        <MaterialIcons name={icon} size={28} color="white" />
      </View>
      <Text style={styles.tileLabel} numberOfLines={2}>{label}</Text>
    </Pressable>
  );

  const MenuSection = ({ title, children }) => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.grid}>
        {children}
      </View>
    </View>
  );

  const PickerCard = ({ icon, label, color = "#007AFF", children }) => (
    <View style={styles.pickerCard}>
      <View style={styles.pickerHeader}>
        <View style={[styles.pickerIconContainer, { backgroundColor: color }]}>
          <MaterialIcons name={icon} size={20} color="white" />
        </View>
        <Text style={styles.pickerLabel}>{label}</Text>
      </View>
      {children}
    </View>
  );

  const handleLogout = async () => {
    try {
      await AsyncStorage.removeItem("operatorId");
      setOperatorId(null);
    } catch (ex) { console.warn("Logout error:", ex); }
    try {
      await AsyncStorage.removeItem("token");
      setToken(null);
    } catch (ex) { console.warn("Logout error:", ex); }
    try {
      await AsyncStorage.removeItem("adminToken");
      setAdminToken(null);
    } catch (ex) { console.warn("Logout error:", ex); }
    try {
      await AsyncStorage.removeItem("location");
      setLocationId(null);
    } catch (ex) { console.warn("Logout error:", ex); }
  };

  return (
    <ScrollView style={styles.scrollView}>
      <View style={styles.container}>
        {token ? (
          <>
            {/* Quick Settings */}
            <View style={styles.pickersRow}>
              <PickerCard icon="location-on" label="Location" color="#4CAF50">
                <Picker
                  selectedValue={locationId}
                  style={styles.picker}
                  onValueChange={async (value) => {
                    try {
                      await AsyncStorage.setItem("location", value);
                      setLocationId(value);
                      if (Platform.OS === "web" && typeof window !== "undefined" && window.location?.reload) {
                        window.location.reload();
                      }
                    } catch (ex) { console.warn("Failed to save location:", ex); }
                  }}
                >
                  <Picker.Item label="All locations" value="All" />
                  {data?.locations?.map((l) => (
                    <Picker.Item
                      key={l.id}
                      label={l.name + " " + l.city}
                      value={l.id}
                    />
                  ))}
                </Picker>
              </PickerCard>

              <PickerCard icon="swipe" label="Swipe Action" color="#FF9800">
                <Picker
                  selectedValue={swipeAction}
                  style={styles.picker}
                  onValueChange={async (value) => {
                    try {
                      await AsyncStorage.setItem("orderItemSwipe", value);
                      setSwipeAction(value);
                      if (Platform.OS === "web" && typeof window !== "undefined" && window.location?.reload) {
                        window.location.reload();
                      }
                    } catch (ex) { console.warn("Failed to save swipe action:", ex); }
                  }}
                >
                  <Picker.Item label="Deliver" value="Deliver" />
                  <Picker.Item label="Delete" value="Delete" />
                </Picker>
              </PickerCard>
            </View>

            {/* Quick Actions */}
            <MenuSection title="Quick Actions">
              <GridTile
                icon="point-of-sale"
                label="Cash Register"
                color="#4CAF50"
                bgColor="#e8f5e9"
                onPress={() => linkTo("/magia/operator-settings/cash-register")}
              />
            </MenuSection>

            {/* Reports Section */}
            {adminToken ? (
              <MenuSection title="Reports & Analytics">
                <GridTile
                  icon="summarize"
                  label="Summary"
                  color="#2196F3"
                  bgColor="#e3f2fd"
                  onPress={() => linkTo("/magia/operator-settings/summary")}
                />
                <GridTile
                  icon="assessment"
                  label="Report"
                  color="#3F51B5"
                  bgColor="#e8eaf6"
                  onPress={() => linkTo("/magia/operator-settings/report")}
                />
                <GridTile
                  icon="bar-chart"
                  label="Sales Charts"
                  color="#009688"
                  bgColor="#e0f2f1"
                  onPress={() => linkTo("/magia/operator-settings/sales-charts")}
                />
              </MenuSection>
            ) : null}

            {/* Business Section */}
            {adminToken ? (
              <MenuSection title="Business">
                <GridTile
                  icon="receipt"
                  label="Receipts"
                  color="#FF5722"
                  bgColor="#fbe9e7"
                  onPress={() => linkTo("/magia/operator-settings/receipts")}
                />
                <GridTile
                  icon="description"
                  label="Invoices"
                  color="#795548"
                  bgColor="#efebe9"
                  onPress={() => linkTo("/magia/operator-settings/invoices")}
                />
                <GridTile
                  icon="local-shipping"
                  label="Suppliers"
                  color="#00695c"
                  bgColor="#e0f2f1"
                  onPress={() => linkTo("/magia/operator-settings/suppliers")}
                />
                <GridTile
                  icon="inventory-2"
                  label="Inventory"
                  color="#1565c0"
                  bgColor="#e3f2fd"
                  onPress={() => linkTo("/magia/operator-settings/inventory")}
                />
                <GridTile
                  icon="fact-check"
                  label="Checkpoints"
                  color="#e65100"
                  bgColor="#fff3e0"
                  onPress={() => linkTo("/magia/operator-settings/inventory-checkpoints")}
                />
              </MenuSection>
            ) : null}

            {/* Menu Management */}
            {adminToken ? (
              <MenuSection title="Menu Management">
                <GridTile
                  icon="design-services"
                  label="Designer"
                  color="#E91E63"
                  bgColor="#fce4ec"
                  onPress={() => linkTo("/magia/operator-settings/designer")}
                />
                {role === "admin" ? (
                  <>
                    <GridTile
                      icon="category"
                      label="Categories"
                      color="#9C27B0"
                      bgColor="#f3e5f5"
                      onPress={() => linkTo("/magia/operator-settings/categories")}
                    />
                    <GridTile
                      icon="inventory"
                      label="Products"
                      color="#673AB7"
                      bgColor="#ede7f6"
                      onPress={() => linkTo("/magia/operator-settings/products")}
                    />
                  </>
                ) : null}
              </MenuSection>
            ) : null}

            {/* Team Section */}
            {role === "admin" && adminToken ? (
              <MenuSection title="Team">
                <GridTile
                  icon="people"
                  label="Operators"
                  color="#00BCD4"
                  bgColor="#e0f7fa"
                  onPress={() => linkTo("/magia/operator-settings/operators")}
                />
                <GridTile
                  icon="person-add"
                  label="Invite"
                  color="#03A9F4"
                  bgColor="#e1f5fe"
                  onPress={() => linkTo("/magia/operator-settings/invite")}
                />
                <GridTile
                  icon="devices"
                  label="Kiosk Devices"
                  color="#7C3AED"
                  bgColor="#f3e8ff"
                  onPress={() => linkTo("/magia/operator-settings/kiosk-devices")}
                />
              </MenuSection>
            ) : null}

            {/* Settings & Account */}
            <MenuSection title="Settings & Account">
              <GridTile
                icon="palette"
                label="Lights & Colors"
                color="#FF9800"
                bgColor="#fff3e0"
                onPress={() => linkTo("/magia/operator-settings/lights&colors")}
              />
              <GridTile
                icon="color-lens"
                label="Theme Designer"
                color="#D81B60"
                bgColor="#fce4ec"
                onPress={() => linkTo("/magia/operator-settings/theme-designer")}
              />
              {passkeySupported ? (
                <GridTile
                  icon="fingerprint"
                  label="Passkeys"
                  color="#4CAF50"
                  bgColor="#e8f5e9"
                  onPress={() => linkTo("/magia/operator-settings/passkeys")}
                />
              ) : null}
              <GridTile
                icon="logout"
                label="Logout"
                color="#f44336"
                bgColor="#ffebee"
                onPress={handleLogout}
              />
            </MenuSection>
          </>
        ) : (
          <View style={styles.loginContainer}>
            <View style={styles.loginCard}>
              <MaterialIcons name="lock" size={48} color={Config.companyColor || '#007AFF'} style={{ marginBottom: 20 }} />
              <Text style={styles.loginTitle}>Sign In</Text>
              <TextInput
                onChangeText={setUsername}
                onSubmitEditing={async (e) => {
                  await tryLogin(e.nativeEvent.text, password);
                }}
                placeholder="Username"
                placeholderTextColor="#999"
                defaultValue=""
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                keyboardType="email-address"
                textContentType="emailAddress"
                style={[styles.input, { color: colorScheme === "dark" ? "white" : "black" }]}
              />
              <TextInput
                onChangeText={setPassword}
                onSubmitEditing={async (e) => {
                  await tryLogin(username, e.nativeEvent.text);
                }}
                secureTextEntry={true}
                placeholder="Password"
                placeholderTextColor="#999"
                defaultValue=""
                style={[styles.input, { color: colorScheme === "dark" ? "white" : "black" }]}
              />
              <Pressable
                style={styles.loginButton}
                onPress={async () => {
                  await tryLogin(username, password);
                }}
              >
                <Text style={styles.loginButtonText}>Login</Text>
              </Pressable>
              {passkeySupported && (
                <Pressable
                  style={styles.passkeyButton}
                  onPress={loginWithPasskey}
                >
                  <MaterialIcons name="fingerprint" size={20} color="white" style={{ marginRight: 8 }} />
                  <Text style={styles.passkeyButtonText}>Login with Passkey</Text>
                </Pressable>
              )}
              {errorMessage ? (
                <Text style={styles.errorText}>{errorMessage}</Text>
              ) : null}
            </View>
          </View>
        )}
        <StatusBar style={Platform.OS === "ios" ? "light" : "auto"} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: '#f2f2f7',
  },
  container: {
    flex: 1,
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
    marginLeft: 4,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
  },
  gridTile: {
    width: '30.33%',
    marginHorizontal: '1.5%',
    marginBottom: 12,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  tileIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  tileLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  pickersRow: {
    flexDirection: 'row',
    marginBottom: 24,
    marginHorizontal: -6,
  },
  pickerCard: {
    flex: 1,
    marginHorizontal: 6,
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  pickerIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  pickerLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  picker: {
    marginLeft: -8,
    marginRight: -8,
  },
  loginContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loginCard: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8,
  },
  loginTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 24,
  },
  input: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f5f5f5',
    fontSize: 16,
    marginBottom: 12,
  },
  loginButton: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#007AFF',
    alignItems: 'center',
    marginTop: 8,
  },
  loginButtonText: {
    color: 'white',
    fontSize: 17,
    fontWeight: '600',
  },
  passkeyButton: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#34c759',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  passkeyButtonText: {
    color: 'white',
    fontSize: 17,
    fontWeight: '600',
  },
  errorText: {
    color: '#ff3b30',
    marginTop: 16,
    textAlign: 'center',
    fontSize: 14,
  },
});
